import { supabase } from '../lib/supabase';
import { MatchLineup, UILineupPlayer, MatchFormation } from '../types/database';

export const lineupService = {
  /**
   * Fetches all players in a team's squad for a given tournament and optional season.
   * @param teamId The ID of the team.
   * @param tournamentId The ID of the tournament.
   * @param seasonId Optional: The ID of the season.
   * @returns A list of players in the squad.
   */
  async getTeamSquad(teamId: string, tournamentId: string, seasonId?: string): Promise<UILineupPlayer[]> {
    console.log(`Fetching squad for team: ${teamId}, tournament: ${tournamentId}, season: ${seasonId || 'N/A'}`);
    
    let query = supabase
      .from('team_squad')
      .select(`
        *,
        players (id, full_name, profile_image, position, kit_number, nationality, date_of_birth)
      `)
      .eq('team_id', teamId)
      .eq('tournament_id', tournamentId)
      .eq('is_active', true);

    if (seasonId) {
      query = query.eq('season_id', seasonId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching team squad:', error);
      throw error;
    }

    // Transform to UILineupPlayer format
    const uiPlayers: UILineupPlayer[] = (data || []).map(squad => ({
      ...squad,
      isSelected: false,
      isCaptain: false,
      isStarting: false,
      isBench: false,
      player: squad.players, // Preserve the player relation
    }));

    console.log('Fetched team squad:', uiPlayers);
    return uiPlayers;
  },

  /**
   * Fetches the match lineup for a specific match and team.
   * @param matchId The ID of the match.
   * @param teamId The ID of the team.
   * @returns A list of players in the match lineup.
   */
  async getMatchLineup(matchId: string, teamId: string): Promise<MatchLineup[]> {
    console.log(`Fetching match lineup for match: ${matchId}, team: ${teamId}`);
    
    const { data, error } = await supabase
      .from('match_lineup')
      .select(`
        *,
        player:players!match_lineup_player_id_fkey (id, full_name, profile_image, position, kit_number, nationality, date_of_birth)
      `)
      .eq('match_id', matchId)
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching match lineup:', error);
      throw error;
    }
    
    console.log('Fetched match lineup:', data);
    return data as MatchLineup[];
  },

  /**
   * Gets match formation and manager info for a team in a match
   * @param matchId The ID of the match.
   * @param teamId The ID of the team.
   * @returns Formation and manager data
   */
  async getMatchFormation(matchId: string, teamId: string): Promise<MatchFormation | null> {
    console.log(`Fetching match formation for match: ${matchId}, team: ${teamId}`);
    
    const { data, error } = await supabase
      .from('match_formations')
      .select('*')
      .eq('match_id', matchId)
      .eq('team_id', teamId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error fetching match formation:', error);
      throw error;
    }
    
    console.log('Fetched match formation:', data);
    return data as MatchFormation | null;
  },

  /**
   * Creates or updates match lineup entries.
   * This function will handle inserting new entries, updating existing ones,
   * and potentially deleting entries if a player is unselected.
   * @param matchId The ID of the match.
   * @param teamId The ID of the team.
   * @param lineupUpdates An array of lineup data to be processed.
   */
  async updateMatchLineup(
    matchId: string,
    teamId: string,
    lineupUpdates: {
      playerId: string;
      isCaptain: boolean;
      isStarting: boolean;
      isBench: boolean;
      isSelected: boolean;
      identificationDescription?: string;
      identificationScreenshotUrl?: string;
    }[]
  ) {
    console.log(`Updating match lineup for match: ${matchId}, team: ${teamId}`);
    
        const existingLineup = await this.getMatchLineup(matchId, teamId);

    const operations = lineupUpdates.map(async (update) => {
      const {
        playerId,
        isCaptain,
        isStarting,
        isBench,
        isSelected,
        identificationDescription,
        identificationScreenshotUrl,
      } = update;
      const existingEntry = existingLineup?.find(item => item.player_id === playerId);

      if (isSelected) {
        if (existingEntry) {
          // Update existing entry
          console.log(`Updating player ${playerId} in lineup.`);
          const { error } = await supabase
            .from('match_lineup')
            .update({ 
              is_captain: isCaptain, 
              is_starting: isStarting,
              is_bench: isBench,
              identification_description: identificationDescription,
              identification_screenshot_url: identificationScreenshotUrl,
              updated_at: new Date().toISOString() 
            })
            .eq('id', existingEntry.id);
          if (error) throw error;
        } else {
          // Insert new entry
          console.log(`Inserting player ${playerId} into lineup.`);
          const { error } = await supabase
            .from('match_lineup')
            .insert({ 
              match_id: matchId, 
              team_id: teamId, 
              player_id: playerId, 
              is_captain: isCaptain, 
              is_starting: isStarting,
              is_bench: isBench,
              identification_description: identificationDescription,
              identification_screenshot_url: identificationScreenshotUrl,
            });
          if (error) throw error;
        }
      } else {
        // If player is not selected but exists in lineup, delete them
        if (existingEntry) {
          console.log(`Deleting player ${playerId} from lineup.`);
          const { error } = await supabase
            .from('match_lineup')
            .delete()
            .eq('id', existingEntry.id);
          if (error) throw error;
        }
      }
    });

    await Promise.all(operations);
    console.log('Match lineup updated successfully.');
  },

  /**
   * Updates match formation and manager info
   * @param matchId The ID of the match.
   * @param teamId The ID of the team.
   * @param formation The formation string (e.g., "4-3-3").
   * @param managerName The manager's name.
   */
  async updateMatchFormation(matchId: string, teamId: string, formation: string, managerName?: string) {
    console.log(`Updating match formation for match: ${matchId}, team: ${teamId}`);
    
    const existingFormation = await this.getMatchFormation(matchId, teamId);

    if (existingFormation) {
      // Update existing formation
      console.log(`Updating existing formation for team ${teamId}.`);
      const { error } = await supabase
        .from('match_formations')
        .update({ 
          formation, 
          manager_name: managerName,
          updated_at: new Date().toISOString() 
        })
        .eq('id', existingFormation.id);
      if (error) throw error;
    } else {
      // Insert new formation
      console.log(`Inserting new formation for team ${teamId}.`);
      const { error } = await supabase
        .from('match_formations')
        .insert({ 
          match_id: matchId, 
          team_id: teamId, 
          formation, 
          manager_name: managerName 
        });
      if (error) throw error;
    }
    
    console.log('Match formation updated successfully.');
  },

  /**
   * Adds a player to a team's squad for a tournament/season
   * @param playerId The ID of the player.
   * @param teamId The ID of the team.
   * @param tournamentId The ID of the tournament.
   * @param seasonId Optional: The ID of the season.
   * @param kitNumber Optional: The player's kit number.
   * @param position Optional: The player's position.
   */
  async addPlayerToSquad(playerId: string, teamId: string, tournamentId: string, seasonId?: string, kitNumber?: number, position?: string) {
    console.log(`Adding player ${playerId} to team ${teamId} squad.`);
    
    const { error } = await supabase
      .from('team_squad')
      .insert({ 
        player_id: playerId,
        team_id: teamId,
        tournament_id: tournamentId,
        season_id: seasonId,
        kit_number: kitNumber,
        position: position,
        is_active: true
      });

    if (error) {
      console.error('Error adding player to squad:', error);
      throw error;
    }
    
    console.log('Player added to squad successfully.');
  },

  /**
   * Removes a player from a team's squad
   * @param squadId The ID of the squad entry.
   */
  async removePlayerFromSquad(squadId: string) {
    console.log(`Removing player from squad: ${squadId}.`);
    
    const { error } = await supabase
      .from('team_squad')
      .update({ is_active: false })
      .eq('id', squadId);

    if (error) {
      console.error('Error removing player from squad:', error);
      throw error;
    }
    
    console.log('Player removed from squad successfully.');
  },
};
